import { memo } from 'react'
import { SxProps } from '@mui/material'
import { Box, Grid } from '@/components/Layout'
import { Text, WarningBox } from '@/components/Data'
import { Modal } from '@/components/Surface'
import { currencies } from '../CurrencyAndLocation.data'
import Button from '../../Button'
import { currencyAndLocationModalStyles as styles } from './CurrencyAndLocationModal.style'
import { CurrencyAndLocationModalProps } from './CurrencyAndLocationModal.types'
import { useCurrencyAndLocationModalController } from './CurrencyAndLocationModal.controller'

function CurrencyAndLocationModal({
  open,
  onClose,
  data,
  onSelectCurrency,
}: CurrencyAndLocationModalProps) {
  const {
    t,
    draftCurrency,
    chargeCurrency,
    shouldShowWarning,
    onSelectDraft,
    onConfirm,
    onCancel,
  } = useCurrencyAndLocationModalController({ data, onClose, onSelectCurrency })

  return (
    <Modal
      title={'Currency'}
      containerStyles={styles.container}
      open={open}
      onClose={onCancel}
      showCloseIcon
      showTitleOnTop
      actionButtonProps={{
        topBordered: true,
        wrapperProps: { sx: styles.actions.wrapper },
        primaryActionText: t('currencyAndLocation.modal.confirm'),
        onPrimaryActionClick: onConfirm,
        primaryButtonProps: { fullWidth: true, size: 'small', sx: styles.actions.confirmButton },
        secondaryActionText: t('currencyAndLocation.modal.cancel'),
        onSecondaryActionClick: onCancel,
        secondaryButtonProps: {
          fullWidth: true,
          size: 'small',
          variant: 'outlined',
          color: 'secondary',
          sx: styles.actions.cancelButton,
        },
      }}>
      <>
        <Box sx={styles.list}>
          <Grid container spacing={1.5}>
            {currencies.map((currency) => (
              <Grid size={6} key={currency.value}>
                <Button
                  fullWidth
                  size={'small'}
                  onClick={() => onSelectDraft(currency.value)}
                  variant="contained"
                  color={'secondary'}
                  sx={
                    {
                      ...styles.currencyButton.root,
                      ...(currency.value === draftCurrency
                        ? { ...styles.currencyButton.active }
                        : {}),
                    } as SxProps
                  }
                  startIcon={<Box component={currency.flag} sx={styles.currencyButton.icon} />}>
                  <Text sx={styles.currencyButton.text}>{currency.value}</Text>
                </Button>
              </Grid>
            ))}
          </Grid>
        </Box>

        {shouldShowWarning && (
          <WarningBox>
            {t('currencyAndLocation.modal.nonBaseCurrencyWarning', {
              currency: draftCurrency,
              chargeCurrency,
            })}
          </WarningBox>
        )}
      </>
    </Modal>
  )
}

export default memo(CurrencyAndLocationModal)
